// ─────────────────────────────────────────────────────────────────────────────
// RulesSettings
//
// Per-account trading rules management.
// User selects an account, then configures rules for THAT account.
// Rules are scoped to (user_id, account_id).
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { useUserRules } from "../../hooks/useUserRules";
import { useCustomPresets } from "../../hooks/useCustomPresets";
import { useViewport } from "../../hooks/useViewport";
import {
  RULE_TYPES,
  RULE_PRESETS,
  defaultParamsFor,
  listRuleCatalog,
} from "../../lib/rules";

export function RulesSettings({ userId, accounts, currentAccountId, setCurrentAccountId }) {
  const navigate = useNavigate();

  // Defensive: ensure accounts is always an array (App may pass null briefly during boot).
  // Wrapped in useMemo so its reference is stable — otherwise useEffect/useMemo hooks that
  // depend on accountsList would re-run on every render even when accounts hasn't changed.
  const accountsList = useMemo(
    () => Array.isArray(accounts) ? accounts : [],
    [accounts]
  );

  // Local state for which account's rules we're editing.
  // Initialized to the dashboard's current account; user can change here too.
  const [activeAccountId, setActiveAccountId] = useState(currentAccountId || (accountsList[0]?.id));

  useEffect(() => {
    if (!activeAccountId && accountsList.length > 0) {
      setActiveAccountId(accountsList[0].id);
    }
  }, [accountsList, activeAccountId]);

  const activeAccount = useMemo(
    () => accountsList.find(a => a.id === activeAccountId) || null,
    [accountsList, activeAccountId]
  );

  const {
    rules, loading,
    addRule, updateRule, deleteRule, toggleRule, applyPreset, applyCustomPreset,
  } = useUserRules(userId, activeAccountId);

  const {
    presets: customPresets, loading: customPresetsLoading,
    createPreset, deletePreset: deleteCustomPreset,
  } = useCustomPresets(userId);

  const [showAddPicker, setShowAddPicker] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [showPresets, setShowPresets] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [presetResult, setPresetResult] = useState(null);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);

  const rulesByCategory = useMemo(() => {
    const grouped = { limits: [], timing: [], events: [] };
    for (const r of rules) {
      const def = RULE_TYPES[r.rule_type];
      const cat = def?.category || "other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(r);
    }
    return grouped;
  }, [rules]);

  const handleApplyPreset = async (presetId) => {
    setApplyingPreset(presetId);
    const result = await applyPreset(presetId);
    if (result?.skipped?.length > 0) {
      setPresetResult(result);
    }
    setApplyingPreset(null);
    setShowPresets(false);
  };

  const handleApplyCustomPreset = async (preset) => {
    setApplyingPreset(`custom:${preset.id}`);
    const result = await applyCustomPreset(preset);
    if (result?.skipped?.length > 0) {
      setPresetResult(result);
    }
    setApplyingPreset(null);
    setShowPresets(false);
  };

  const handleDeleteCustomPreset = async (presetId) => {
    if (!window.confirm("Delete this preset? This can't be undone.")) return;
    await deleteCustomPreset(presetId);
  };

  const handleSavePreset = async (name, description) => {
    setSavingPreset(true);
    const created = await createPreset(name, rules, description);
    setSavingPreset(false);
    if (created) setShowSavePreset(false);
    return created;
  };

  // ── EARLY RETURNS ────────────────────────────────────────────────────

  if (accountsList.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <BackLink onClick={() => navigate("/profile")} />
        <Card>
          <h2 style={{ fontSize: 18, color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
            No accounts yet
          </h2>
          <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.6 }}>
            Trading rules are scoped to a specific account. Add at least one account
            in your profile before configuring rules.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px 60px", maxWidth: 1000, margin: "0 auto" }}>
      <Header
        onBack={() => navigate("/profile")}
        ruleCount={rules.length}
      />

      <AccountSelector
        accounts={accountsList}
        activeAccountId={activeAccountId}
        onChange={setActiveAccountId}
        ruleCount={rules.length}
      />

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
          Loading rules for {activeAccount?.name || "this account"}…
        </div>
      ) : rules.length === 0 ? (
        <EmptyState
          accountName={activeAccount?.name || "this account"}
          onApplyPreset={() => setShowPresets(true)}
          onAddCustom={() => setShowAddPicker(true)}
        />
      ) : (
        <>
          <ActionRow
            onApplyPreset={() => setShowPresets(true)}
            onAddCustom={() => setShowAddPicker(true)}
            onSavePreset={() => setShowSavePreset(true)}
          />

          {rulesByCategory.limits.length > 0 && (
            <CategorySection
              title="Daily Limits"
              icon="🎯"
              rules={rulesByCategory.limits}
              editingRuleId={editingRuleId}
              setEditingRuleId={setEditingRuleId}
              onUpdate={updateRule}
              onDelete={deleteRule}
              onToggle={toggleRule}
            />
          )}

          {rulesByCategory.timing.length > 0 && (
            <CategorySection
              title="Time Windows"
              icon="⏰"
              rules={rulesByCategory.timing}
              editingRuleId={editingRuleId}
              setEditingRuleId={setEditingRuleId}
              onUpdate={updateRule}
              onDelete={deleteRule}
              onToggle={toggleRule}
            />
          )}

          {rulesByCategory.events.length > 0 && (
            <CategorySection
              title="Event Blackouts"
              icon="📰"
              rules={rulesByCategory.events}
              editingRuleId={editingRuleId}
              setEditingRuleId={setEditingRuleId}
              onUpdate={updateRule}
              onDelete={deleteRule}
              onToggle={toggleRule}
            />
          )}
        </>
      )}

      {showAddPicker && (
        <AddRuleModal
          existingRules={rules}
          onClose={() => setShowAddPicker(false)}
          onAdd={async (ruleType) => {
            await addRule({
              rule_type: ruleType,
              parameters: defaultParamsFor(ruleType),
              severity: "warn",
            });
            setShowAddPicker(false);
          }}
        />
      )}

      {presetResult && (
        <div style={{
          margin: "0 0 14px",
          padding: "10px 14px",
          background: presetResult.skipped.length > 0 && presetResult.added.length === 0
            ? C.red + "15"
            : C.green + "15",
          border: `1px solid ${presetResult.skipped.length > 0 && presetResult.added.length === 0 ? C.red : C.green}40`,
          borderRadius: 8,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.6,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 10,
        }}>
          <div>
            {presetResult.added.length > 0 && (
              <div>✓ Added {presetResult.added.length} rule{presetResult.added.length === 1 ? "" : "s"}</div>
            )}
            {presetResult.skipped.length > 0 && (
              <div style={{ color: C.yellow }}>⚠ Skipped {presetResult.skipped.length} duplicate{presetResult.skipped.length === 1 ? "" : "s"}</div>
            )}
          </div>
          <button
            onClick={() => setPresetResult(null)}
            style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, lineHeight: 1 }}
          >✕</button>
        </div>
      )}
      {showPresets && (
        <PresetsModal
          accountName={activeAccount?.name || ""}
          onClose={() => setShowPresets(false)}
          onApply={handleApplyPreset}
          applyingId={applyingPreset}
          existingRules={rules}
          customPresets={customPresets}
          customPresetsLoading={customPresetsLoading}
          onApplyCustom={handleApplyCustomPreset}
          onDeleteCustom={handleDeleteCustomPreset}
        />
      )}

      {showSavePreset && (
        <SavePresetModal
          ruleCount={rules.length}
          saving={savingPreset}
          onClose={() => setShowSavePreset(false)}
          onSave={handleSavePreset}
        />
      )}

      <Disclaimer />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────────────────────────────────────────

function Header({ onBack, ruleCount }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <BackLink onClick={onBack} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h1 style={{ fontSize: 28, color: C.text, margin: 0, fontWeight: 800, letterSpacing: "-0.02em" }}>
          Trading Rules
        </h1>
        <div style={{ fontSize: 12, color: C.muted, fontFamily: "monospace" }}>
          {ruleCount} active
        </div>
      </div>
      <p style={{ color: C.sub, fontSize: 14, margin: "8px 0 0", lineHeight: 1.5 }}>
        Set the rules you want to trade by. Rules are configured per account —
        a TopStep combine, an Apex eval, and a funded account can each have
        different discipline.
      </p>
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none", border: "none", color: C.muted,
        cursor: "pointer", marginBottom: 12, fontSize: 13, padding: 0,
      }}
    >
      ← Profile
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNT SELECTOR
// ─────────────────────────────────────────────────────────────────────────────

function AccountSelector({ accounts, activeAccountId, onChange, ruleCount }) {
  const active = accounts.find(a => a.id === activeAccountId);

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{
        display: "block", fontSize: 11, color: C.muted,
        textTransform: "uppercase", letterSpacing: "0.08em",
        fontWeight: 700, marginBottom: 6,
      }}>
        Configuring rules for
      </label>
      <select
        value={activeAccountId || ""}
        onChange={e => onChange(e.target.value)}
        style={{
          padding: "12px 14px",
          background: "#1a1d2e",
          border: `1px solid ${C.border}`,
          borderRadius: 8, color: C.text,
          minWidth: 320, fontSize: 14,
          cursor: "pointer",
        }}
      >
        {accounts.map(a => (
          <option key={a.id} value={a.id}>
            {a.name} — {fmt$(Number(a.starting_balance || 0))}
            {a.account_type ? ` (${a.account_type})` : ""}
          </option>
        ))}
      </select>
      {active && (
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
          {ruleCount > 0
            ? `${ruleCount} rule${ruleCount === 1 ? "" : "s"} on this account.`
            : "No rules yet on this account — start with a preset below."}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────────────────────────────────────

function EmptyState({ accountName, onApplyPreset, onAddCustom }) {
  return (
    <Card style={{ padding: 32, textAlign: "center", marginTop: 20 }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
      <h2 style={{ fontSize: 18, color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
        No rules on {accountName}
      </h2>
      <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.6, margin: "0 0 20px", maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
        Start with a preset bundle (calibrated for popular prop firms) or build your own rule
        from scratch. Rules apply only to this account.
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          onClick={onApplyPreset}
          style={{
            padding: "12px 20px", background: C.blue,
            color: "#fff", border: "none", borderRadius: 8,
            fontWeight: 700, cursor: "pointer", fontSize: 14,
          }}
        >
          Start with a preset
        </button>
        <button
          onClick={onAddCustom}
          style={{
            padding: "12px 20px", background: "transparent",
            color: C.text, border: `1px solid ${C.border}`, borderRadius: 8,
            fontWeight: 600, cursor: "pointer", fontSize: 14,
          }}
        >
          Add a custom rule
        </button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTION ROW
// ─────────────────────────────────────────────────────────────────────────────

function ActionRow({ onApplyPreset, onAddCustom, onSavePreset }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
      <button
        onClick={onAddCustom}
        style={{
          padding: "10px 16px", background: C.blue, color: "#fff",
          border: "none", borderRadius: 8,
          fontWeight: 700, cursor: "pointer", fontSize: 13,
        }}
      >
        + Add Rule
      </button>
      <button
        onClick={onApplyPreset}
        style={{
          padding: "10px 16px", background: "transparent", color: C.muted,
          border: `1px solid ${C.border}`, borderRadius: 8,
          fontWeight: 600, cursor: "pointer", fontSize: 13,
        }}
      >
        Apply a preset
      </button>
      <button
        onClick={onSavePreset}
        style={{
          padding: "10px 16px", background: "transparent", color: C.muted,
          border: `1px solid ${C.border}`, borderRadius: 8,
          fontWeight: 600, cursor: "pointer", fontSize: 13,
        }}
      >
        Save as preset
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORY SECTION
// ─────────────────────────────────────────────────────────────────────────────

function CategorySection({ title, icon, rules, editingRuleId, setEditingRuleId, onUpdate, onDelete, onToggle }) {
  return (
    <Card style={{ marginBottom: 16, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.08em", fontWeight: 700, textTransform: "uppercase" }}>
          {icon} {title}
        </div>
      </div>
      {rules.map(rule => (
        <RuleRow
          key={rule.id}
          rule={rule}
          editing={editingRuleId === rule.id}
          onStartEdit={() => setEditingRuleId(rule.id)}
          onCancelEdit={() => setEditingRuleId(null)}
          onSaveEdit={async (updates) => {
            await onUpdate(rule.id, updates);
            setEditingRuleId(null);
          }}
          onDelete={() => onDelete(rule.id)}
          onToggle={() => onToggle(rule.id, !rule.enabled)}
        />
      ))}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RULE ROW
// ─────────────────────────────────────────────────────────────────────────────

function RuleRow({ rule, editing, onStartEdit, onCancelEdit, onSaveEdit, onDelete, onToggle }) {
  const { isMobile } = useViewport();
  const def = RULE_TYPES[rule.rule_type];
  if (!def) {
    return (
      <div style={{ padding: 12, color: C.red, fontSize: 12, borderTop: `1px solid ${C.border}` }}>
        Unknown rule type: {rule.rule_type}
      </div>
    );
  }

  if (editing) {
    return (
      <RuleEditor
        rule={rule}
        def={def}
        onSave={onSaveEdit}
        onCancel={onCancelEdit}
      />
    );
  }

  return (
    <div style={{
      padding: "14px 16px",
      borderTop: `1px solid ${C.border}`,
      display: "grid",
      gridTemplateColumns: isMobile ? "auto 1fr" : "auto 1fr auto auto auto",
      gap: isMobile ? 10 : 12,
      alignItems: isMobile ? "flex-start" : "center",
      opacity: rule.enabled ? 1 : 0.55,
    }}>
      <button
        onClick={onToggle}
        style={{
          width: 36, height: 20, borderRadius: 10,
          border: "none", padding: 0,
          background: rule.enabled ? C.green : C.border,
          cursor: "pointer", position: "relative",
          transition: "background 0.2s",
        }}
        title={rule.enabled ? "Click to disable" : "Click to enable"}
      >
        <span style={{
          position: "absolute",
          left: rule.enabled ? 18 : 2,
          top: 2, width: 16, height: 16,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.2s",
        }} />
      </button>

      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
          {def.icon} {rule.custom_label || def.label}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontFamily: "monospace" }}>
          {summarizeParams(rule.parameters, def)}
        </div>
        {/* On mobile: severity + actions live below the label in the same cell */}
        {isMobile && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
            <div style={{
              fontSize: 10, padding: "3px 8px", borderRadius: 4,
              background: rule.severity === "warn" ? C.yellow + "22" : C.muted + "22",
              color: rule.severity === "warn" ? C.yellow : C.muted,
              fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {rule.severity}
            </div>
            <button onClick={onStartEdit} style={{
              background: "transparent", border: `1px solid ${C.border}`,
              color: C.muted, borderRadius: 6, padding: "4px 8px",
              fontSize: 11, cursor: "pointer", fontWeight: 600,
            }}>Edit</button>
            <button onClick={() => { if (window.confirm("Delete this rule? You can re-add it later.")) onDelete(); }}
              style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", fontSize: 14, padding: "0 4px" }}
              title="Delete rule">🗑️</button>
          </div>
        )}
      </div>

      {/* Desktop only: severity + actions as separate grid columns */}
      {!isMobile && (
        <>
          <div style={{
            fontSize: 10, padding: "3px 8px", borderRadius: 4,
            background: rule.severity === "warn" ? C.yellow + "22" : C.muted + "22",
            color: rule.severity === "warn" ? C.yellow : C.muted,
            fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {rule.severity}
          </div>
          <button onClick={onStartEdit} style={{
            background: "transparent", border: `1px solid ${C.border}`,
            color: C.muted, borderRadius: 6, padding: "6px 10px",
            fontSize: 11, cursor: "pointer", fontWeight: 600,
          }}>Edit</button>
          <button onClick={() => { if (window.confirm("Delete this rule? You can re-add it later.")) onDelete(); }}
            style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", fontSize: 14, padding: "0 8px" }}
            title="Delete rule">🗑️</button>
        </>
      )}
    </div>
  );
}

function summarizeParams(params, def) {
  if (!params || !def?.params) return "";
  const parts = [];
  for (const p of def.params) {
    const val = params[p.name];
    if (val === undefined || val === null) continue;
    if (p.type === "currency") parts.push(`${p.label}: $${val}`);
    else if (Array.isArray(val)) parts.push(`${p.label}: ${val.join(", ")}`);
    else parts.push(`${p.label}: ${val}`);
  }
  return parts.join(" • ");
}

// ─────────────────────────────────────────────────────────────────────────────
// RULE EDITOR
// ─────────────────────────────────────────────────────────────────────────────

function RuleEditor({ rule, def, onSave, onCancel }) {
  const [params, setParams] = useState({ ...rule.parameters });
  const [severity, setSeverity] = useState(rule.severity);
  const [customLabel, setCustomLabel] = useState(rule.custom_label || "");

  const handleSave = () => {
    onSave({ parameters: params, severity, custom_label: customLabel || null });
  };

  return (
    <div style={{
      padding: "16px 16px 18px",
      borderTop: `1px solid ${C.border}`,
      background: "rgba(0,0,0,0.2)",
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>
        {def.icon} Editing: {def.label}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {def.params.map(p => (
          <ParamInput
            key={p.name}
            param={p}
            value={params[p.name]}
            onChange={v => setParams({ ...params, [p.name]: v })}
          />
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", fontSize: 11, color: C.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Custom label (optional)
        </label>
        <input
          type="text"
          value={customLabel}
          onChange={e => setCustomLabel(e.target.value)}
          placeholder={def.label}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 11, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Severity
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          {["warn", "silent"].map(s => (
            <button
              key={s}
              onClick={() => setSeverity(s)}
              style={{
                padding: "8px 16px", borderRadius: 6,
                border: `1px solid ${severity === s ? C.blue : C.border}`,
                background: severity === s ? C.blue + "22" : "transparent",
                color: severity === s ? C.blue : C.muted,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {s === "warn" ? "Show me a warning" : "Track silently"}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
          {severity === "warn"
            ? "We'll show a yellow notice on the dashboard and Trade Entry when this rule is violated."
            : "Violations are tracked in your analytics but no UI warning shows up."}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={handleSave}
          style={{
            padding: "10px 18px", background: C.blue, color: "#fff",
            border: "none", borderRadius: 6,
            fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          Save changes
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: "10px 18px", background: "transparent", color: C.muted,
            border: `1px solid ${C.border}`, borderRadius: 6,
            fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PARAM INPUT
// ─────────────────────────────────────────────────────────────────────────────

function ParamInput({ param, value, onChange }) {
  const labelStyle = {
    display: "block", fontSize: 11, color: C.muted, marginBottom: 4,
    textTransform: "uppercase", letterSpacing: "0.05em",
  };

  if (param.type === "int" || param.type === "currency") {
    return (
      <div>
        <label style={labelStyle}>{param.label}</label>
        <input
          type="number"
          value={value ?? param.default}
          min={param.min}
          max={param.max}
          onChange={e => onChange(Number(e.target.value))}
          style={inputStyle}
        />
      </div>
    );
  }

  if (param.type === "time") {
    return (
      <div>
        <label style={labelStyle}>{param.label}</label>
        <input
          type="time"
          value={value || param.default}
          onChange={e => onChange(e.target.value)}
          style={inputStyle}
        />
      </div>
    );
  }

  if (param.type === "string") {
    return (
      <div>
        <label style={labelStyle}>{param.label}</label>
        <input
          type="text"
          value={value ?? param.default ?? ""}
          onChange={e => onChange(e.target.value)}
          style={inputStyle}
        />
      </div>
    );
  }

  if (param.type === "multiselect") {
    const current = Array.isArray(value) ? value : (param.default || []);
    const toggle = (opt) => {
      onChange(current.includes(opt) ? current.filter(x => x !== opt) : [...current, opt]);
    };
    return (
      <div style={{ gridColumn: "span 2" }}>
        <label style={labelStyle}>{param.label}</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {param.options.map(opt => (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              style={{
                padding: "6px 12px", borderRadius: 999,
                border: `1px solid ${current.includes(opt) ? C.blue : C.border}`,
                background: current.includes(opt) ? C.blue + "22" : "transparent",
                color: current.includes(opt) ? C.blue : C.muted,
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

const inputStyle = {
  width: "100%", padding: "8px 10px",
  background: "#0b0d19", border: `1px solid ${C.border}`,
  borderRadius: 6, color: C.text, fontSize: 13,
  boxSizing: "border-box",
};

// ─────────────────────────────────────────────────────────────────────────────
// ADD RULE MODAL
// ─────────────────────────────────────────────────────────────────────────────

function AddRuleModal({ existingRules, onClose, onAdd }) {
  const catalog = useMemo(() => listRuleCatalog(), []);

  const isMultiple = (def) => def.multiple;
  const alreadyExists = (ruleType) => existingRules.some(r => r.rule_type === ruleType);

  return (
    <ModalShell onClose={onClose} title="Add a rule">
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 16, lineHeight: 1.5 }}>
        Pick a rule type to add. You'll be able to customize the parameters.
      </div>

      {Object.entries(catalog).map(([category, defs]) => (
        <div key={category} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.08em", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
            {category}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {defs.map(def => {
              const exists = alreadyExists(def.id);
              const canAdd = isMultiple(def) || !exists;
              return (
                <button
                  key={def.id}
                  onClick={() => canAdd && onAdd(def.id)}
                  disabled={!canAdd}
                  style={{
                    padding: "12px 14px",
                    background: canAdd ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)",
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    textAlign: "left",
                    cursor: canAdd ? "pointer" : "not-allowed",
                    opacity: canAdd ? 1 : 0.5,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                    {def.icon} {def.label}
                    {!canAdd && (
                      <span style={{ fontSize: 10, color: C.muted, marginLeft: 8, fontWeight: 400 }}>
                        (already added)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.sub, marginTop: 3, lineHeight: 1.4 }}>
                    {def.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRESETS MODAL
// ─────────────────────────────────────────────────────────────────────────────

function PresetsModal({
  accountName, onClose, onApply, applyingId, existingRules = [],
  customPresets = [], customPresetsLoading = false, onApplyCustom, onDeleteCustom,
}) {
  return (
    <ModalShell onClose={onClose} title={`Apply a preset to ${accountName}`}>
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 16, lineHeight: 1.5 }}>
        Each preset adds a calibrated set of rules to this account. You can edit or
        remove individual rules afterward. Applying a preset doesn't remove your
        existing rules.
      </div>
      {existingRules.length > 0 && (
        <div style={{
          fontSize: 11, color: C.yellow,
          background: C.yellow + "12",
          border: `1px solid ${C.yellow}40`,
          borderRadius: 6, padding: "8px 12px",
          marginBottom: 14, lineHeight: 1.5,
        }}>
          ⚠ Rules marked <strong>already exists</strong> won't be added again to prevent duplicates.
        </div>
      )}

      {/* ── YOUR PRESETS (custom, user-created) ───────────────────────── */}
      {!customPresetsLoading && customPresets.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.08em", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
            Your Presets
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {customPresets.map(preset => (
              <div key={preset.id} style={{
                padding: 14,
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, gap: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                    {preset.name}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => onApplyCustom(preset)}
                      disabled={applyingId === `custom:${preset.id}`}
                      style={{
                        padding: "6px 14px", background: C.blue, color: "#fff",
                        border: "none", borderRadius: 6,
                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                        opacity: applyingId === `custom:${preset.id}` ? 0.6 : 1,
                      }}
                    >
                      {applyingId === `custom:${preset.id}` ? "Applying..." : "Apply"}
                    </button>
                    <button
                      onClick={() => onDeleteCustom(preset.id)}
                      title="Delete this preset"
                      style={{
                        padding: "6px 10px", background: "transparent", color: C.muted,
                        border: `1px solid ${C.border}`, borderRadius: 6,
                        fontSize: 12, cursor: "pointer",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {preset.description && (
                  <div style={{ fontSize: 12, color: C.sub, marginBottom: 10, lineHeight: 1.5 }}>
                    {preset.description}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {(preset.rules || []).map((r, i) => {
                    const def = RULE_TYPES[r.rule_type];
                    const allowsMultiple = def?.multiple === true;
                    const alreadyExists = existingRules.some(e => {
                      if (e.rule_type !== r.rule_type) return false;
                      if (!allowsMultiple) return true;
                      return JSON.stringify(e.parameters) === JSON.stringify(r.parameters);
                    });
                    return (
                      <div key={i} style={{ fontSize: 11, color: alreadyExists ? C.yellow : C.muted, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 5 }}>
                        {def?.icon} {def?.short_label || r.rule_type}: {summarizeParamsInline(r.parameters)}
                        {alreadyExists && (
                          <span style={{ fontSize: 10, color: C.yellow, fontWeight: 600 }}>⚠ already exists — will be skipped</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!customPresetsLoading && customPresets.length === 0 && (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
          You haven't saved any custom presets yet. Configure rules on an account,
          then use <strong style={{ color: C.sub }}>Save as preset</strong> to reuse
          them on other accounts.
        </div>
      )}

      <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.08em", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
        Built-in Presets
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {Object.values(RULE_PRESETS).map(preset => (
          <div key={preset.id} style={{
            padding: 14,
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                {preset.name}
              </div>
              <button
                onClick={() => onApply(preset.id)}
                disabled={applyingId === preset.id}
                style={{
                  padding: "6px 14px", background: C.blue, color: "#fff",
                  border: "none", borderRadius: 6,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  opacity: applyingId === preset.id ? 0.6 : 1,
                }}
              >
                {applyingId === preset.id ? "Applying..." : "Apply"}
              </button>
            </div>
            <div style={{ fontSize: 12, color: C.sub, marginBottom: 10, lineHeight: 1.5 }}>
              {preset.description}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {preset.rules.map((r, i) => {
                const def = RULE_TYPES[r.rule_type];
                const allowsMultiple = def?.multiple === true;
                const alreadyExists = existingRules.some(e => {
                  if (e.rule_type !== r.rule_type) return false;
                  if (!allowsMultiple) return true;
                  return JSON.stringify(e.parameters) === JSON.stringify(r.parameters);
                });
                return (
                  <div key={i} style={{ fontSize: 11, color: alreadyExists ? C.yellow : C.muted, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 5 }}>
                    {def?.icon} {def?.short_label || r.rule_type}: {summarizeParamsInline(r.parameters)}
                    {alreadyExists && (
                      <span style={{ fontSize: 10, color: C.yellow, fontWeight: 600 }}>⚠ already exists — will be skipped</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

function summarizeParamsInline(params) {
  if (!params) return "";
  return Object.entries(params).map(([k, v]) => {
    if (Array.isArray(v)) return `${k}=${v.join(",")}`;
    return `${k}=${v}`;
  }).join(", ");
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVE PRESET MODAL
// ─────────────────────────────────────────────────────────────────────────────

function SavePresetModal({ ruleCount, saving, onClose, onSave }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setErr("Give the preset a name.");
      return;
    }
    setErr(null);
    const created = await onSave(name, description);
    if (!created) setErr("Couldn't save the preset. Try again.");
  };

  return (
    <ModalShell onClose={onClose} title="Save current rules as a preset">
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 16, lineHeight: 1.5 }}>
        Saves all {ruleCount} rule{ruleCount === 1 ? "" : "s"} on this account as a
        reusable preset. You can apply it to any account — including ones you
        create later.
      </div>

      <label style={{ display: "block", fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>
        Preset name
      </label>
      <input
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="e.g. My Combine Rules"
        style={{ ...inputStyle, marginBottom: 14 }}
      />

      <label style={{ display: "block", fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>
        Description <span style={{ textTransform: "none", fontWeight: 400 }}>(optional)</span>
      </label>
      <input
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="What's this preset for?"
        style={{ ...inputStyle, marginBottom: 14 }}
      />

      {err && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 14 }}>{err}</div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          onClick={onClose}
          style={{
            padding: "10px 16px", background: "transparent", color: C.muted,
            border: `1px solid ${C.border}`, borderRadius: 8,
            fontWeight: 600, cursor: "pointer", fontSize: 13,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "10px 16px", background: C.blue, color: "#fff",
            border: "none", borderRadius: 8,
            fontWeight: 700, cursor: "pointer", fontSize: 13,
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving..." : "Save Preset"}
        </button>
      </div>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL SHELL
// ─────────────────────────────────────────────────────────────────────────────

function ModalShell({ children, title, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        paddingTop: "5vh", paddingBottom: "5vh",
        zIndex: 1000, overflowY: "auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
          padding: 20, width: "min(560px, 92vw)",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, color: C.text, margin: 0, fontWeight: 700 }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "none", color: C.muted,
              fontSize: 20, cursor: "pointer", padding: 0, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DISCLAIMER
// ─────────────────────────────────────────────────────────────────────────────

function Disclaimer() {
  return (
    <div style={{
      marginTop: 24, padding: 14,
      background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`,
      borderRadius: 8, fontSize: 11, color: C.muted, lineHeight: 1.6,
    }}>
      <strong style={{ color: C.sub }}>How rules work.</strong> Rules are advisory by design.
      The app will show you when you're approaching or breaking a rule — on the dashboard,
      on the Trade Entry form, and in your analytics — but it will never block you from logging
      a trade. The journal mirrors what actually happened in your broker. The discipline
      comes from awareness, not enforcement.
    </div>
  );
}
